'use client';

/**
 * Task 15 — Floating visitor chat widget ("Sunny").
 *
 * A launcher bubble (bottom-right) that opens a chat panel, streams from
 * /api/chat (hand-rolled fetch/ReadableStream reader — no @ai-sdk/react, F9),
 * renders assistant replies with clickable /courses/<slug> links, and offers an
 * opt-in lead form (explicit consent) via `visitorChat.submitLead`.
 *
 * Copy is currently English (the parent-facing i18n pass is a separate effort);
 * it lives under PublicI18nProvider so it can be localized later.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { useTRPC } from '@/lib/trpc/provider';
import { TurnstileWidget } from '@/components/auth/turnstile-widget';

type Msg = { role: 'user' | 'assistant'; content: string };

const GREETING =
  "Hi, I'm Sunny 👋 — ask me anything about EiFO Academy, or tell me about your child and I'll help you find the right class.";

const GRADE_OPTIONS = [
  'kindergarten', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th',
  'middle_school', 'high_school',
] as const;

const INTEREST_OPTIONS = [
  'reading', 'writing', 'public_speaking', 'math', 'technology',
  '3d_modeling', 'esl', 'world_languages',
] as const;

function gradeLabel(g: string): string {
  if (g === 'kindergarten') return 'Kindergarten';
  if (g === 'middle_school') return 'Middle school';
  if (g === 'high_school') return 'High school';
  return `${g} grade`;
}

function interestLabel(i: string): string {
  return i.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Linkify markdown links [label](/path) + bare internal paths in a segment. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const linkRe = /\[([^\]]+)\]\((\/[^\s)]+)\)|(\/(?:courses|blog|enrollment|contact|refund-policy|educators)[^\s)]*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const label = m[1];
    const href = m[2] ?? m[3];
    const display = label ?? href;
    nodes.push(
      <a
        key={`${keyPrefix}-a${key++}`}
        href={href}
        className="text-eifo-orange underline underline-offset-2 hover:text-eifo-orange/80"
      >
        {display}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/**
 * Render assistant text: markdown **bold** as real bold, links clickable, and
 * stray heading markers stripped — so the raw markdown characters the model
 * emits never show up literally.
 */
function AssistantText({ text }: { text: string }) {
  // Strip leading markdown heading hashes (### Title → Title) line-by-line;
  // the model occasionally emits them and we render as plain/bold text.
  const cleaned = text.replace(/^\s{0,3}#{1,6}\s+/gm, '');

  const nodes: React.ReactNode[] = [];
  const boldRe = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = boldRe.exec(cleaned)) !== null) {
    if (m.index > last) nodes.push(...renderInline(cleaned.slice(last, m.index), `t${key}`));
    nodes.push(
      <strong key={`b${key}`} className="font-semibold">
        {renderInline(m[1], `bi${key}`)}
      </strong>
    );
    last = m.index + m[0].length;
    key++;
  }
  if (last < cleaned.length) nodes.push(...renderInline(cleaned.slice(last), `t${key}`));
  return <span className="whitespace-pre-wrap break-words">{nodes}</span>;
}

export function VisitorChatWidget({ turnstileSiteKey }: { turnstileSiteKey?: string }) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, showLeadForm, open]);

  const needsTurnstile = !conversationId && !!turnstileSiteKey;
  const canSend = input.trim().length > 0 && !streaming && (!needsTurnstile || !!turnstileToken);

  async function sendMessage() {
    const text = input.trim();
    if (!text || streaming) return;
    if (needsTurnstile && !turnstileToken) {
      setError('Please complete the verification first.');
      return;
    }
    setError(null);
    // Build the model history from user/assistant turns (skip the local greeting).
    const history = messages.filter((_, i) => !(i === 0 && messages[0].content === GREETING));
    const payloadMessages = [...history, { role: 'user' as const, content: text }];

    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: conversationId ?? undefined,
          turnstileToken: conversationId ? undefined : turnstileToken,
          messages: payloadMessages,
        }),
      });

      const convId = res.headers.get('X-Conversation-Id');
      if (convId) setConversationId(convId);

      const contentType = res.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        // Error path (validation / turnstile / rate-limit / conversation cap).
        const data = (await res.json()) as { error?: { code?: string; message?: string } };
        const msg = data.error?.message ?? 'Something went wrong. Please try again.';
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: msg };
          return copy;
        });
        // A failed Turnstile means we need a fresh token.
        if (res.status === 403) setTurnstileToken('');
        return;
      }

      if (!res.body) {
        const fallback = await res.text();
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: fallback || '…' };
          return copy;
        });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: acc };
          return copy;
        });
      }
      if (!acc.trim()) {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: '…' };
          return copy;
        });
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: 'assistant',
          content: 'Sorry — I had trouble connecting. Please try again.',
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        aria-label="Chat with Sunny"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full bg-eifo-orange px-4 py-3 text-white shadow-lg transition hover:bg-eifo-orange/90 motion-reduce:transition-none"
      >
        <MessageCircle className="h-5 w-5" aria-hidden />
        <span className="text-sm font-medium">Chat with Sunny</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex h-[min(600px,80vh)] w-[min(400px,92vw)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between bg-eifo-primary px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-sm font-bold">Su</span>
          <div>
            <p className="text-sm font-semibold leading-none">Sunny</p>
            <p className="text-xs text-white/70">EiFO Academy assistant</p>
          </div>
        </div>
        <button type="button" aria-label="Close chat" onClick={() => setOpen(false)} className="rounded p-1 hover:bg-white/10">
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={
                m.role === 'user'
                  ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-eifo-primary px-3 py-2 text-sm text-white'
                  : 'max-w-[85%] rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-800'
              }
            >
              {m.role === 'assistant' && m.content === '' ? (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Sunny is typing" />
              ) : m.role === 'assistant' ? (
                <AssistantText text={m.content} />
              ) : (
                <span className="whitespace-pre-wrap break-words">{m.content}</span>
              )}
            </div>
          </div>
        ))}

        {showLeadForm && (
          <LeadForm conversationId={conversationId} onClose={() => setShowLeadForm(false)} />
        )}
      </div>

      {/* Footer / input */}
      <div className="border-t border-gray-100 p-3">
        {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
        {needsTurnstile && (
          <div className="mb-2">
            <TurnstileWidget siteKey={turnstileSiteKey} onVerify={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
          </div>
        )}
        {!showLeadForm && conversationId && (
          <button
            type="button"
            onClick={() => setShowLeadForm(true)}
            className="mb-2 text-xs font-medium text-eifo-orange hover:text-eifo-orange/80"
          >
            Have our team reach out →
          </button>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSend) void sendMessage();
              }
            }}
            rows={1}
            placeholder="Ask about a course…"
            maxLength={4000}
            className="max-h-28 flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-eifo-orange focus:outline-none"
          />
          <button
            type="button"
            aria-label="Send message"
            disabled={!canSend}
            onClick={() => void sendMessage()}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-eifo-orange text-white transition hover:bg-eifo-orange/90 disabled:opacity-40"
          >
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Inline opt-in lead form ──

function LeadForm({
  conversationId,
  onClose,
}: {
  conversationId: string | null;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [childName, setChildName] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [goals, setGoals] = useState('');
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);

  const mutation = useMutation(
    trpc.visitorChat.submitLead.mutationOptions({
      onSuccess: () => setDone(true),
    })
  );

  if (done) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">
        Thanks! Our team will reach out soon. 🎉
      </div>
    );
  }

  const canSubmit = name.trim() && email.trim() && consent && !!conversationId && !mutation.isPending;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 text-sm shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <p className="font-semibold text-gray-800">Have our team reach out</p>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Dismiss form">
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <div className="space-y-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name *" maxLength={100}
          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 focus:border-eifo-orange focus:outline-none" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Your email *" maxLength={254}
          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 focus:border-eifo-orange focus:outline-none" />
        <input value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="Child's name (optional)" maxLength={100}
          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 focus:border-eifo-orange focus:outline-none" />
        <select value={gradeLevel} onChange={(e) => setGradeLevel(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 focus:border-eifo-orange focus:outline-none">
          <option value="">Child's grade (optional)</option>
          {GRADE_OPTIONS.map((g) => (
            <option key={g} value={g}>{gradeLabel(g)}</option>
          ))}
        </select>
        <div className="flex flex-wrap gap-1">
          {INTEREST_OPTIONS.map((i) => {
            const active = interests.includes(i);
            return (
              <button
                key={i}
                type="button"
                onClick={() => setInterests((prev) => (active ? prev.filter((x) => x !== i) : [...prev, i]))}
                className={
                  active
                    ? 'rounded-full bg-eifo-orange px-2 py-0.5 text-xs text-white'
                    : 'rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:border-eifo-orange'
                }
              >
                {interestLabel(i)}
              </button>
            );
          })}
        </div>
        <textarea value={goals} onChange={(e) => setGoals(e.target.value)} rows={2} maxLength={1000}
          placeholder="What are your goals? (optional)"
          className="w-full resize-none rounded-lg border border-gray-300 px-2 py-1.5 focus:border-eifo-orange focus:outline-none" />
        <label className="flex items-start gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          <span>I agree to be contacted by EiFO Academy about courses. *</span>
        </label>
        {mutation.isError && (
          <p className="text-xs text-red-600">Couldn&apos;t submit. Please check your details and try again.</p>
        )}
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() =>
            conversationId &&
            mutation.mutate({
              conversationId,
              name: name.trim(),
              email: email.trim(),
              childName: childName.trim() || undefined,
              gradeLevel: (gradeLevel || undefined) as never,
              interestedCourses: interests.length ? (interests as never) : undefined,
              goals: goals.trim() || undefined,
              consentAcknowledged: true,
            })
          }
          className="w-full rounded-lg bg-eifo-orange py-2 text-sm font-medium text-white transition hover:bg-eifo-orange/90 disabled:opacity-40"
        >
          {mutation.isPending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
